import React, { useState } from "react";
import * as S from "./styles";
import RoundInput from "@/components/RoundInput";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TablePagination,
} from "@mui/material";
import { MdOutlineGroups, MdOutlineSearch } from "react-icons/md";
import { fetchGetAllUsers } from "@/hooks/fetchHooks/adminFetch";
import { FaAngleDown } from "react-icons/fa6";
import { useQueryClient } from "@tanstack/react-query";
import { fetchCountAffiliates } from "@/hooks/fetchHooks/affiliatesFetch";
import { FutButton } from "@/components/FutButton";
import { CountUserChildren } from "@/interfaces/user";
import { AiFillStar } from "react-icons/ai";
import { useForm } from "react-hook-form";
import { IoIosCloseCircleOutline } from "react-icons/io";

export default function UserManager() {
  const [pagination, setPagination] = useState({ page: 0, pageSize: 20 });
  const [search, setSearch] = useState("");
  const [id, setId] = useState(1);
  const queryClient = useQueryClient();

  const allUsers = fetchGetAllUsers(
    pagination.page,
    pagination.pageSize,
    search,
    {
      meta: { errorMessage: "Ocorreu um erro ao buscar os usuários" },
    }
  );

  const affiliates = fetchCountAffiliates(id, {
    meta: { errorMessage: "Ocorreu um erro ao buscar os afiliados" },
  });

  const formatMoney = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const userForm = useForm({
    defaultValues: {
      searchInput: "",
    },
  });

  const watchSearch = userForm.watch("searchInput");

  const handlePagination = (
    action: "ROW_CHANGE" | "PAGE_CHANGE",
    newValue: number
  ) => {
    if (action === "PAGE_CHANGE") {
      setPagination((prev) => ({ ...prev, page: newValue }));
    }
    if (action === "ROW_CHANGE") {
      setPagination((prev) => ({ ...prev, pageSize: newValue }));
    }
  };

  const fetchAffCounts = (id: number) => {
    setId(id);
  };

  const handleSearch = () => {
    setSearch(userForm.getValues("searchInput"));
  };

  return (
    <>
      <S.SearchWrapper>
        <RoundInput
          size="small"
          endAdornment={
            <InputAdornment position="end">
              {watchSearch ? (
                <IconButton
                  onClick={() => {
                    userForm.setValue("searchInput", "");
                    handleSearch()
                  }}
                  edge="end"
                >
                  <IoIosCloseCircleOutline />
                </IconButton>
              ) : (
                <MdOutlineSearch />
              )}
            </InputAdornment>
          }
          sx={{ mr: 1 }}
          control={userForm.control}
          inputName="searchInput"
          placeholder="Pesquisar email ou nome"
        />
        <FutButton onClick={handleSearch}>Pesquisar</FutButton>
      </S.SearchWrapper>
      {allUsers.isLoading ? (
        <CircularProgress />
      ) : (
        allUsers.data?.data.map((el) => (
          <Accordion
            sx={(props) => ({
              mt: 2,
              backgroundColor: props.palette.backgroundColor.main,
            })}
            key={el.id}
          >
            <AccordionSummary
              expandIcon={<FaAngleDown />}
              aria-controls={`panel${el.id}-content`}
              id={`panel${el.id}-header`}
            >
              {el.name}
            </AccordionSummary>
            <AccordionDetails>
              <S.PaymentWrapper>
                <S.PaymentTable>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Telefone</th>
                      <th>Cpf</th>
                      <th>Dinheiro</th>
                      <th>Endereço</th>
                      <th>Chave Pix</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{el.email}</td>
                      <td>{el.phone}</td>
                      <td>{el.cpf}</td>
                      <td>
                        {formatMoney.format(el.balance)} / pendente:{" "}
                        {formatMoney.format(el.pending_balance)}
                      </td>
                      <td>
                        {el.city} / {el.cep} / {el.address}
                      </td>
                      <td>{el.pix_key}</td>
                    </tr>
                  </tbody>
                </S.PaymentTable>
              </S.PaymentWrapper>
              {queryClient.getQueryData(["user/countChildUsers", el.id]) ? (
                <S.AffiliatedLevelContainer>
                  <>
                    {Array(3)
                      .fill("")
                      .map((_, i) => {
                        const children = `child_lvl_${
                          i + 1
                        }` as keyof CountUserChildren;
                        return (
                          <S.AffiliatedLevel key={i}>
                            <span>
                              {Array(i + 1)
                                .fill("")
                                .map((_, i) => (
                                  <AiFillStar key={i} />
                                ))}
                            </span>
                            <MdOutlineGroups />
                            <p>{i + 1}° Nível</p>
                            <div>
                              <span>
                                {
                                  (
                                    queryClient.getQueryData([
                                      "user/countChildUsers",
                                      el.id,
                                    ]) as CountUserChildren
                                  )?.[children].count
                                }{" "}
                                pessoas
                              </span>
                              <span>
                                {new Intl.NumberFormat("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                }).format(
                                  (
                                    queryClient.getQueryData([
                                      "user/countChildUsers",
                                      el.id,
                                    ]) as CountUserChildren
                                  )?.[children].total_earned || 0
                                )}
                              </span>
                              <span>
                                {new Intl.NumberFormat("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                }).format(
                                  (
                                    queryClient.getQueryData([
                                      "user/countChildUsers",
                                      el.id,
                                    ]) as CountUserChildren
                                  )?.[children].payment_pending || 0
                                )}
                              </span>
                            </div>
                          </S.AffiliatedLevel>
                        );
                      })}
                  </>
                </S.AffiliatedLevelContainer>
              ) : (
                <Grid container justifyContent="center" pb={2}>
                  {affiliates.isLoading ? (
                    <CircularProgress />
                  ) : (
                    <FutButton onClick={() => fetchAffCounts(el.id)}>
                      Ver Status Afiliados
                    </FutButton>
                  )}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}
      <TablePagination
        component="div"
        sx={{ ".MuiTablePagination-selectLabel": { display: "none" } }}
        rowsPerPageOptions={[20, 50, 100, 500, 1000]}
        count={allUsers.data?.allUsersCount ?? 0}
        rowsPerPage={pagination.pageSize}
        page={pagination.page}
        onPageChange={(e, newPage) => handlePagination("PAGE_CHANGE", newPage)}
        onRowsPerPageChange={(e) => {
          handlePagination("ROW_CHANGE", parseInt(e.target.value));
        }}
      />
    </>
  );
}
